import React, { useEffect, useRef, useState } from "react"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { TAsset } from "../types"
import { graphql, useStaticQuery } from "gatsby"
import * as styles from "./contributors-cycle.css"
import { truncate } from "../utils"
import {
  Button,
} from "./ui"

type ContributorsCycleProps = {
  title: string
  id: string
  features: {
    title: string
    summary: string
    slug: string
    id: string
    avatar: TAsset
    largeProfileImage?: TAsset
  }[]
}

export default function ContributorsCycle(props: ContributorsCycleProps) {
  const [activeContributor, setActiveContributor] = useState(1)

  const avatarScroll = useRef(null)
  const activeProfileRef = useRef(null)
  const activeAvatarRef = useRef(null)
  const activeCardRef = useRef(null)

  const intervalSpeed = 5000

  const avatars = props.features.map((contributor, i: number) => {
    const active = i === activeContributor
    return (
      <div 
        className={styles.avatarContainer} 
        id={`${props.id}-${i}`}
        key={contributor.id} 
        ref={active ? activeAvatarRef : null} 
        data-selected={active ? true : false}
        data-out={false}
      >
        <GatsbyImage
          data-ref={`ref-${i}`}
          key={contributor.id}
          className={styles.avatarImage}
          alt={contributor.title}
          image={getImage(contributor.avatar)}
        />
      </div>
    )
  })

  const nextContributor = () => {
    const limit = avatars.length - 1
    const popContributor = activeContributor == 0 ? document.getElementById(`${props.id}-${limit}`) : document.getElementById(`${props.id}-${activeContributor - 1}`)
    const nextContributor = activeContributor == limit ? 0 : activeContributor + 1
    avatarScroll.current.dataset.scroll = "true"
    popContributor.dataset.out = "true"
    setActiveContributor(nextContributor)
    setTimeout(() => {
      avatarScroll.current.dataset.scroll = "false"
      popContributor.dataset.out = "false"
      popContributor.parentNode.appendChild(popContributor)
    }, 500)
  }


  useEffect(() => {
    const interval = setInterval(() => { 
      nextContributor()
    }, intervalSpeed)
    return () => clearInterval(interval)
  }, [activeContributor, avatars])

  const defaultBackgroundQuery = useStaticQuery(
    graphql`
      query {
        background: file(relativePath: { eq: "cycle-background.jpg" }) {
          childImageSharp {
            gatsbyImageData(
              width: 800
              placeholder: BLURRED
              formats: [AUTO]
            )
          }
        }
      }
    `
)
  const cycleBackground = defaultBackgroundQuery.background

  const profiles = props.features.map((contributor, i: number) => {
    const active = i === activeContributor
    const profileImage = contributor.largeProfileImage ? contributor.largeProfileImage: contributor.avatar
    return (
      <div className={styles.profileContainer} key={contributor.id} ref={active ? activeProfileRef : null} data-selected={active ? true : false}>
        <GatsbyImage
          className={styles.profileImage}
          alt={contributor.title || 'Contributor'}
          image={getImage(profileImage)}
        />
      </div>
    )
  })

  const cards = props.features.map((contributor, i: number) => {
    const active = i === activeContributor
    const extype = contributor.id.replace(/-|[0-9]|Card-|DatoCms/g, '').toLowerCase()
    const type = extype == "artist" ? extype + 's' : extype

    return (
      <div className={styles.card} key={contributor.id} ref={active ? activeCardRef : null} data-selected={active ? true : false}>
        <h3 className={styles.cardTitle}>{contributor.title || null}</h3>
        <div className={styles.cardSummary}>{truncate(contributor.summary, 80)}</div>
        <div className={styles.buttonContainer}>
          <Button className={styles.cardButton} to={`/${type}/${contributor.slug}`}>{contributor.title}'s Page</Button>
        </div>
      </div>
    )
  })

  return (
    <div className={styles.cycle}>
      <div className={styles.cycleBackground}>
        <div className={styles.cycleBackgroundInner}>
          <GatsbyImage
            className={styles.backgroundImage}
            alt="Contributors Cycle Background"
            image={getImage(cycleBackground)}
          />
        </div>
      </div>
      <h2 className={styles.cycleTitle}>{props.title}</h2>
      <div className={styles.profiles}>
        {profiles}
      </div>
      <div className={styles.avatars}>
        <div className={styles.avatarsStrip} ref={avatarScroll} data-scroll={false}>
          {avatars}
        </div>
      </div>
      <div className={styles.cards}>
        {cards}
      </div>
    </div>
  )
}
